import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Drawer,
  Typography,
  TextField,
  IconButton,
  List,
  ListItem,
  Avatar,
  Divider
} from '@mui/material';
import {
  Close as CloseIcon,
  Send as SendIcon
} from '@mui/icons-material';

function ChatPanel({ 
  open, 
  onClose, 
  messages, 
  onSendMessage,
  currentParticipantId 
}) {
  const [newMessage, setNewMessage] = useState('');
  const messagesEndRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const handleSend = () => {
    if (newMessage.trim()) {
      onSendMessage(newMessage);
      setNewMessage('');
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const formatTime = (timestamp) => {
    const date = new Date(timestamp);
    return date.toLocaleTimeString('en-US', { 
      hour: '2-digit', 
      minute: '2-digit' 
    });
  };

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      sx={{
        '& .MuiDrawer-paper': {
          width: 320,
          bgcolor: '#2a2a2a',
          color: 'white',
          display: 'flex',
          flexDirection: 'column'
        }
      }}
    >
      {/* Header */}
      <Box 
        sx={{ 
          p: 2, 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center',
          flexShrink: 0
        }}
      >
        <Typography variant="h6">
          Chat
        </Typography>
        <IconButton onClick={onClose} sx={{ color: 'white' }}>
          <CloseIcon />
        </IconButton>
      </Box>

      <Divider sx={{ borderColor: '#444' }} />

      {/* Messages */}
      <List 
        sx={{ 
          flex: 1, 
          overflow: 'auto', 
          p: 2,
          display: 'flex',
          flexDirection: 'column',
          gap: 2
        }}
      >
        {messages.length === 0 ? (
          <Box 
            sx={{ 
              display: 'flex', 
              justifyContent: 'center', 
              alignItems: 'center',
              height: '100%',
              color: 'rgba(255, 255, 255, 0.5)'
            }}
          >
            <Typography variant="body2">
              No messages yet
            </Typography>
          </Box>
        ) : (
          messages.map((message, index) => {
            const isOwn = message.participant?.id === currentParticipantId;
            
            return (
              <ListItem
                key={message.id || index}
                sx={{
                  p: 0,
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: isOwn ? 'flex-end' : 'flex-start'
                }}
              >
                {!isOwn && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                    <Avatar
                      sx={{ width: 24, height: 24, fontSize: 12 }}
                    >
                      {message.participant?.displayName?.charAt(0).toUpperCase() || '?'}
                    </Avatar>
                    <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                      {message.participant?.displayName || 'Unknown'}
                    </Typography>
                  </Box>
                )}

                <Box
                  sx={{
                    bgcolor: isOwn ? 'primary.main' : 'rgba(255, 255, 255, 0.1)',
                    color: 'white',
                    px: 2,
                    py: 1,
                    borderRadius: 2,
                    maxWidth: '80%',
                    wordBreak: 'break-word'
                  }}
                >
                  <Typography variant="body2">
                    {message.content}
                  </Typography>
                  <Typography 
                    variant="caption" 
                    sx={{ 
                      display: 'block',
                      mt: 0.5,
                      opacity: 0.7,
                      fontSize: 10
                    }}
                  >
                    {formatTime(message.createdAt || Date.now())}
                  </Typography>
                </Box>
              </ListItem>
            );
          })
        )}
        <div ref={messagesEndRef} />
      </List>

      <Divider sx={{ borderColor: '#444' }} />

      {/* Input */}
      <Box 
        sx={{ 
          p: 2, 
          display: 'flex', 
          gap: 1,
          flexShrink: 0
        }}
      >
        <TextField
          fullWidth
          multiline
          maxRows={3}
          value={newMessage}
          onChange={(e) => setNewMessage(e.target.value)}
          onKeyPress={handleKeyPress}
          placeholder="Type a message..."
          variant="outlined"
          size="small"
          sx={{
            '& .MuiOutlinedInput-root': {
              color: 'white',
              '& fieldset': {
                borderColor: 'rgba(255, 255, 255, 0.3)'
              },
              '&:hover fieldset': {
                borderColor: 'rgba(255, 255, 255, 0.5)'
              },
              '&.Mui-focused fieldset': {
                borderColor: 'primary.main'
              }
            }
          }}
        />
        <IconButton
          onClick={handleSend}
          disabled={!newMessage.trim()}
          sx={{
            color: 'white',
            bgcolor: 'primary.main',
            '&:hover': {
              bgcolor: 'primary.dark'
            },
            '&:disabled': {
              bgcolor: 'rgba(255, 255, 255, 0.1)',
              color: 'rgba(255, 255, 255, 0.3)'
            }
          }}
        >
          <SendIcon />
        </IconButton>
      </Box>
    </Drawer>
  );
}

export default ChatPanel;
